/**
 * User Profile Component
 *
 * Displays the current user's profile information in the header/navigation.
 * Shows avatar, name, role, and logout button.
 * Subscribes to auth state changes for updates.
 */

import { renderIcon, createIconButton } from '../utils/icons';
import {
  subscribeToAuth,
  selectUser,
  selectIsAuthenticated,
  type AuthState,
  type User,
} from '../services/auth-store';
import { logout } from '../services/auth-service';

/**
 * User profile component options
 */
export interface UserProfileOptions {
  /** Show role badge (default: true) */
  showRole?: boolean;
  /** Show logout button (default: true) */
  showLogout?: boolean;
  /** Size variant (default: 'md') */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Callback when logout is clicked */
  onLogout?: () => void;
}

/**
 * Create a user avatar element
 */
function createAvatar(user: User, size: 'sm' | 'md' | 'lg'): HTMLElement {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };

  const avatar = document.createElement('div');
  avatar.className = `
    ${sizeClasses[size]}
    rounded-full flex items-center justify-center
    bg-primary/20 text-primary font-medium
    shrink-0
  `.replace(/\s+/g, ' ').trim();

  // If user has a photo URL, use it
  if (user.photoUrl) {
    const img = document.createElement('img');
    img.src = user.photoUrl;
    img.alt = user.displayName;
    img.className = 'w-full h-full rounded-full object-cover';
    img.onerror = () => {
      // Fallback to initials on error
      img.remove();
      avatar.textContent = getInitials(user);
    };
    avatar.appendChild(img);
  } else {
    // Use initials
    avatar.textContent = getInitials(user);
  }

  return avatar;
}

/**
 * Get user initials from display name
 */
function getInitials(user: User): string {
  if (user.givenName && user.surname) {
    return `${user.givenName[0]}${user.surname[0]}`.toUpperCase();
  }
  const parts = user.displayName.split(' ');
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  }
  return user.displayName.substring(0, 2).toUpperCase();
}

/**
 * Create role badge element
 * Returns null for USER role (basic users don't need a role badge)
 */
function createRoleBadge(role: string): HTMLElement | null {
  // Don't show badge for basic USER role
  if (role === 'USER') {
    return null;
  }

  const badge = document.createElement('span');

  const roleColors = {
    ADMIN: 'bg-primary/20 text-primary',
    ASSISTANT: 'bg-muted text-muted-foreground',
  };

  badge.className = `
    inline-flex items-center px-2 py-0.5
    text-xs font-medium rounded-full
    ${roleColors[role as keyof typeof roleColors] ?? roleColors.ASSISTANT}
  `.replace(/\s+/g, ' ').trim();

  badge.textContent = role.charAt(0) + role.slice(1).toLowerCase();

  return badge;
}

/**
 * Create the user profile component
 */
export function createUserProfile(options: UserProfileOptions = {}): HTMLElement {
  const {
    showRole = true,
    showLogout = true,
    size = 'md',
    className = '',
    onLogout,
  } = options;

  const container = document.createElement('div');
  container.className = `user-profile flex items-center gap-3 ${className}`.trim();

  // Inner content container
  const content = document.createElement('div');
  content.id = 'user-profile-content';
  content.className = 'flex items-center gap-3';

  container.appendChild(content);

  // Subscribe to auth state changes
  const unsubscribe = subscribeToAuth((state: AuthState) => {
    const isAuthenticated = selectIsAuthenticated(state);
    const user = selectUser(state);

    // Clear content
    content.innerHTML = '';

    if (isAuthenticated && user) {
      // Avatar
      const avatar = createAvatar(user, size);
      content.appendChild(avatar);

      // User info
      const info = document.createElement('div');
      info.className = 'hidden sm:flex flex-col min-w-0';

      const name = document.createElement('span');
      name.className = 'text-sm font-medium text-foreground truncate';
      name.textContent = user.displayName;
      info.appendChild(name);

      if (showRole) {
        const roleBadge = createRoleBadge(user.role);
        // Only show role container if there's a badge or department
        if (roleBadge || user.department) {
          const roleContainer = document.createElement('div');
          roleContainer.className = 'flex items-center gap-1';
          if (roleBadge) {
            roleContainer.appendChild(roleBadge);
          }
          // Show department if available
          if (user.department) {
            if (roleBadge) {
              const separator = document.createElement('span');
              separator.className = 'text-muted-foreground text-xs';
              separator.textContent = ' · ';
              roleContainer.appendChild(separator);
            }
            const deptSpan = document.createElement('span');
            deptSpan.className = 'text-xs text-muted-foreground truncate';
            deptSpan.textContent = user.department;
            roleContainer.appendChild(deptSpan);
          }
          info.appendChild(roleContainer);
        }
      } else if (user.email) {
        const email = document.createElement('span');
        email.className = 'text-xs text-muted-foreground truncate';
        email.textContent = user.email;
        info.appendChild(email);
      }

      content.appendChild(info);

      // Logout button
      if (showLogout) {
        const logoutBtn = createIconButton('logOut', {
          size: size === 'lg' ? 'md' : 'sm',
          ariaLabel: 'Sign out',
          title: 'Sign out',
          buttonClassName: `
            ml-2 p-2 rounded-lg
            text-muted-foreground hover:text-foreground
            hover:bg-muted transition-colors
          `.replace(/\s+/g, ' ').trim(),
          onClick: async () => {
            if (onLogout) {
              onLogout();
            }
            try {
              await logout();
            } catch (error) {
              console.error('Logout error:', error);
            }
          },
        });
        content.appendChild(logoutBtn);
      }
    }
  });

  // Store unsubscribe function for cleanup
  (container as HTMLElement & { cleanup?: () => void }).cleanup = unsubscribe;

  return container;
}

/**
 * Create a compact user menu (dropdown style)
 */
export function createUserMenu(options: UserProfileOptions = {}): HTMLElement {
  const {
    showRole = true,
    className = '',
    onLogout,
  } = options;

  const container = document.createElement('div');
  container.className = `user-menu relative ${className}`.trim();

  // Trigger button
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = `
    flex items-center gap-2 p-2 rounded-lg
    hover:bg-muted transition-colors
    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
  `.replace(/\s+/g, ' ').trim();
  trigger.id = 'user-menu-trigger';

  // Dropdown menu (hidden by default)
  const dropdown = document.createElement('div');
  dropdown.className = `
    absolute right-0 top-full mt-2 w-64
    glass-card rounded-lg shadow-xl
    hidden z-50 py-2
  `.replace(/\s+/g, ' ').trim();
  dropdown.id = 'user-menu-dropdown';

  container.appendChild(trigger);
  container.appendChild(dropdown);

  // Toggle dropdown on click
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('hidden');
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', () => {
    dropdown.classList.add('hidden');
  });

  // Subscribe to auth state changes
  const unsubscribe = subscribeToAuth((state: AuthState) => {
    const isAuthenticated = selectIsAuthenticated(state);
    const user = selectUser(state);

    // Clear content
    trigger.innerHTML = '';
    dropdown.innerHTML = '';

    if (isAuthenticated && user) {
      // Update trigger
      const avatar = createAvatar(user, 'sm');
      trigger.appendChild(avatar);
      trigger.appendChild(renderIcon('chevronDown', { size: 'xs', className: 'text-muted-foreground' }));

      // Update dropdown content
      // User info section
      const userInfo = document.createElement('div');
      userInfo.className = 'px-4 py-3 border-b border-border';
      userInfo.innerHTML = `
        <div class="font-medium text-foreground">${escapeHtml(user.displayName)}</div>
        <div class="text-sm text-muted-foreground">${escapeHtml(user.email)}</div>
        ${user.department ? `<div class="text-xs text-muted-foreground mt-1">${escapeHtml(user.department)}</div>` : ''}
      `;
      if (showRole) {
        const roleBadge = createRoleBadge(user.role);
        if (roleBadge) {
          const roleContainer = document.createElement('div');
          roleContainer.className = 'mt-2';
          roleContainer.appendChild(roleBadge);
          userInfo.appendChild(roleContainer);
        }
      }
      dropdown.appendChild(userInfo);

      // Menu items
      const menuItems = document.createElement('div');
      menuItems.className = 'py-2';

      // Settings link
      const settingsLink = document.createElement('a');
      settingsLink.href = '/settings';
      settingsLink.className = `
        flex items-center gap-3 px-4 py-2 text-sm
        text-foreground hover:bg-muted transition-colors
      `.replace(/\s+/g, ' ').trim();
      settingsLink.innerHTML = `
        ${renderIcon('settings', { size: 'sm' }).outerHTML}
        <span>Settings</span>
      `;
      menuItems.appendChild(settingsLink);

      // Delegation settings link
      const delegationLink = document.createElement('a');
      delegationLink.href = '/delegation';
      delegationLink.className = `
        flex items-center gap-3 px-4 py-2 text-sm
        text-foreground hover:bg-muted transition-colors
      `.replace(/\s+/g, ' ').trim();
      delegationLink.innerHTML = `
        ${renderIcon('users', { size: 'sm' }).outerHTML}
        <span>Delegation</span>
      `;
      menuItems.appendChild(delegationLink);

      dropdown.appendChild(menuItems);

      // Logout section
      const logoutSection = document.createElement('div');
      logoutSection.className = 'border-t border-border pt-2';

      const logoutBtn = document.createElement('button');
      logoutBtn.type = 'button';
      logoutBtn.className = `
        w-full flex items-center gap-3 px-4 py-2 text-sm
        text-destructive hover:bg-destructive/10 transition-colors
      `.replace(/\s+/g, ' ').trim();
      logoutBtn.innerHTML = `
        ${renderIcon('logOut', { size: 'sm' }).outerHTML}
        <span>Sign out</span>
      `;
      logoutBtn.addEventListener('click', async () => {
        dropdown.classList.add('hidden');
        if (onLogout) {
          onLogout();
        }
        try {
          await logout();
        } catch (error) {
          console.error('Logout error:', error);
        }
      });

      logoutSection.appendChild(logoutBtn);
      dropdown.appendChild(logoutSection);
    }
  });

  // Store unsubscribe function for cleanup
  (container as HTMLElement & { cleanup?: () => void }).cleanup = unsubscribe;

  return container;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Cleanup user profile component
 */
export function cleanupUserProfile(element: HTMLElement): void {
  const cleanup = (element as HTMLElement & { cleanup?: () => void }).cleanup;
  if (cleanup) {
    cleanup();
  }
}
